// src/pages/recruiter-biodata-edit/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import HeaderBar from "components/ui/HeaderBar";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import Icon from "components/AppIcon";
import Notifier from "components/ui/Notifier";
import { useNotifications } from "hooks/useNotifications";
import { biodataAPI } from "utils/api";
import {
  FormField,
  TextInput,
  SelectInput,
  TextAreaInput,
  DateInput,
  NumberWithUnit,
} from "components/ui/FormField";
import FilePreview, { FilePreviewModal } from "components/FilePreview";
import BiodataPreview from "./components/BiodataPreview";
import DocumentUploadRow from './components/DocumentUploadRow';
import Stepper from './components/Stepper';

const documentTypes = [
  { value: "passport_copy", label: "Passport Copy" },
  { value: "next_of_kin_identification", label: "Next of Kin ID" },
  { value: "vaccine_certificate", label: "Skilled Certificate" },
  { value: "medical_certificate", label: "Medical Certificate (FWCMS)" },
  { value: "highest_qualification", label: "Highest Qualification" },
];

// Common nationalities of foreign workers in Malaysia
const nationalityOptions = [
  { value: "Bangladesh", label: "Bangladesh" },
  { value: "Indonesia", label: "Indonesia" },
  { value: "Nepal", label: "Nepal" },
  { value: "India", label: "India" },
  { value: "Pakistan", label: "Pakistan" },
  { value: "Myanmar", label: "Myanmar" },
  { value: "Vietnam", label: "Vietnam" },
  { value: "Philippines", label: "Philippines" },
  { value: "Cambodia", label: "Cambodia" },
  { value: "Sri Lanka", label: "Sri Lanka" },
  { value: "Laos", label: "Laos" },
];

const educationOptions = [
  { value: "Primary", label: "Primary" },
  { value: "Secondary", label: "Secondary" },
  { value: "Diploma", label: "Diploma" },
  { value: "Degree", label: "Degree" },
  { value: "Master", label: "Master" },
  { value: "Vocational/Technical", label: "Vocational/Technical" },
  { value: "Others", label: "Others" },
];

const religionOptions = [
  { value: "Islam", label: "Islam" },
  { value: "Christianity", label: "Christianity" },
  { value: "Hinduism", label: "Hinduism" },
  { value: "Buddhism", label: "Buddhism" },
  { value: "Others", label: "Others" },
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeDateInput = (value) => {
  if (!value) return "";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const mapBiodataToForm = (b = {}) => ({
  fullName: b.full_name || b.fullName || "",
  passportNo: b.passport_no || b.passportNo || "",
  passportPlaceOfIssue: b.passport_place_of_issue || b.passportPlaceOfIssue || "",
  passportExpiryDate: normalizeDateInput(
    b.passport_expiry_date || b.passportExpiryDate
  ),
  nationality: b.nationality || "",
  dateOfBirth: normalizeDateInput(b.date_of_birth || b.dateOfBirth),
  gender: b.gender || "male",
  maritalStatus: b.marital_status || b.maritalStatus || "single",
  placeOfBirth: b.place_of_birth || b.placeOfBirth || "",
  educationLevel: b.education_level || b.educationLevel || "",
  heightCm: b.height_cm || b.heightCm || "",
  weightKg: b.weight_kg || b.weightKg || "",
  religion: b.religion || "",
  email: b.email || "",
  address: b.address || "",
  contactNo: b.contact_no || b.contactNo || "",
  vdrType: b.vdr_type || b.vdrType || "standard",
  medicalExpiryDate: normalizeDateInput(
    b.medical_expiry_date || b.medicalExpiryDate
  ),
  skills: b.skill || b.skills || "",
  experience: b.experience || b.construction_experience || "",
  remarks: b.remarks || "",
  nextOfKinName: b.next_of_kin?.name || b.nextOfKinName || "",
  nextOfKinRelationship:
    b.next_of_kin?.relationship || b.nextOfKinRelationship || "",
  nextOfKinIdentificationNo:
    b.next_of_kin?.identification_no || b.nextOfKinIdentificationNo || "",
  nextOfKinAddress: b.next_of_kin?.address || b.nextOfKinAddress || "",
  nextOfKinContactNo: b.next_of_kin?.contact_no || b.nextOfKinContactNo || "",
  nextOfKinBankAccountNo:
    b.next_of_kin?.bank_account_no || b.nextOfKinBankAccountNo || "",
  nextOfKinBankName: b.next_of_kin?.bank_name || b.nextOfKinBankName || "",
  photos: Array.isArray(b.photos) ? b.photos : [],
  documents: Array.isArray(b.documents) ? b.documents : [],
});

// Helper function to format document type labels
const formatDocumentTypeLabel = (type) => {
  const match = documentTypes.find((d) => d.value === type);
  return match?.label || type?.replace(/_/g, " ") || "";
};

const RecruiterBiodataEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [search] = useSearchParams();
  const isAmend = useMemo(() => search.get("amend") === "1", [search]);
  const draftId = useMemo(() => {
    const d = search.get("draftId") || search.get("id");
    return d || null;
  }, [search]);
  const effectiveId = id || draftId; // when absent => create mode
  const isEditing = !!effectiveId;
  const {
    notifications,
    showSuccess,
    showError,
    showWarning,
    removeNotification,
  } = useNotifications();
  const steps = ["Personal", "Next of Kin", "Documents", "Photos", "Review"];
  const [currentStep, setCurrentStep] = useState(0);
  const storageStepKey = useMemo(
    () => `recruiter_biodata_step_edit_${effectiveId || "new"}`,
    [effectiveId]
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    passportNo: ``,
    passportPlaceOfIssue: "",
    passportExpiryDate: "",
    nationality: "",
    dateOfBirth: "",
    gender: "male",
    maritalStatus: "single",
    placeOfBirth: "",
    educationLevel: "",
    heightCm: "",
    weightKg: "",
    religion: "",
    email: "",
    address: "",
    contactNo: "",
    vdrType: "eQuotaSpecial",
    medicalExpiryDate: "",
    skills: "",
    experience: "",
    remarks: "",
    nextOfKinName: "",
    nextOfKinRelationship: "",
    nextOfKinIdentificationNo: "",
    nextOfKinAddress: "",
    nextOfKinContactNo: "",
    nextOfKinBankAccountNo: "",
    nextOfKinBankName: "",
    documents: [],
    photos: [],
  });
  const [uploadedFiles, setUploadedFiles] = useState([]); // new uploads only
  const [errors, setErrors] = useState({});
  const [originalPassportNo, setOriginalPassportNo] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Restore step from sessionStorage if present
    try {
      const saved = sessionStorage.getItem(storageStepKey);
      const n = saved != null ? Number(saved) : NaN;
      if (!Number.isNaN(n) && n >= 0 && n < steps.length) {
        setCurrentStep(n);
      }
    } catch (_) {}
  }, [storageStepKey, steps.length]);

  useEffect(() => {
    // Persist step to sessionStorage on change
    try {
      sessionStorage.setItem(storageStepKey, String(currentStep));
    } catch (_) {}
  }, [currentStep, storageStepKey]);
  // Load biodata when id changes
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        if (!effectiveId) {
          // Create mode: no initial fetch
          setOriginalPassportNo("");
          return;
        }
        const res = await biodataAPI.getBiodataById(effectiveId);
        if (res.status !== "success")
          throw new Error(res.message || "Failed to load biodata");
        const b = res.data;
        setForm(mapBiodataToForm(b));
        setOriginalPassportNo(b.passport_no || b.passportNo || "");
      } catch (e) {
        showError(e.message || "Failed to load biodata");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [effectiveId, showError]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Open file in modal preview instead of new tab
  const handleFileClick = (file) => {
    if (!file) return;
    setSelectedFile(file);
    setIsModalOpen(true);
  };

  const removeDocument = async (documentId) => {
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api"
        }/biodatas/${effectiveId}/documents/${documentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        // Remove from form state immediately for UI feedback
        setForm((prev) => ({
          ...prev,
          documents: prev.documents.filter((d) => d.id !== documentId),
        }));
        showSuccess("Document removed successfully");
      } else {
        throw new Error("Failed to remove document");
      }
    } catch (error) {
      showError(`Failed to remove document: ${error.message}`);
    }
  };

  const removePhoto = async (photoId) => {
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api"
        }/biodatas/${effectiveId}/photos/${photoId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        // Remove from form state immediately for UI feedback
        setForm((prev) => ({
          ...prev,
          photos: prev.photos.filter((p) => p.id !== photoId),
        }));
        showSuccess("Photo removed successfully");
      } else {
        throw new Error("Failed to remove photo");
      }
    } catch (error) {
      showError(`Failed to remove photo: ${error.message}`);
    }
  };

  const handlePreUpload = async (file, kind, subtype) => {

    // Validate file type - only allow PDF, JPEG, JPG, PNG
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    const fileExt = file?.name?.toLowerCase().split(".").pop();
    const allowedExts = ["pdf", "jpg", "jpeg", "png"];

    if (!allowedTypes.includes(file?.type) && !allowedExts.includes(fileExt)) {
      showError("Only PDF, JPEG, JPG, and PNG files are allowed");
      return;
    }

    // Validate file size (5MB for documents, 2MB for photos)
    const maxSize = kind === "document" ? 5 * 1024 * 1024 : 2 * 1024 * 1024;
    if (file?.size > maxSize) {
      showError(
        `File size exceeds ${kind === "document" ? "5MB" : "2MB"} limit`
      );
      return;
    }

    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await biodataAPI.uploadFile(fd);
      if (res.status !== "success")
        throw new Error(res.message || "Upload failed");
      const f = res.data.file || res.file || res.data;
      // Build friendly file names
      const getDocLabel = (type) => {
        const match = documentTypes.find((d) => d.value === type);
        return match?.label || type;
      };
      const sanitize = (val) =>
        (val || "NA")
          .toString()
          .trim()
          .replace(/[\s()]+/g, "_")
          .replace(/[^a-zA-Z0-9._-]/g, "_");
      const getExt = (name) =>
        name && name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
      const buildDocFileName = (origName) => {
        const ext = getExt(origName);
        const pass = sanitize(form.passportNo || form.passport_no);
        const full = sanitize(form.fullName || form.full_name);
        const label = sanitize(getDocLabel(subtype));
        return `${pass}_${full}_${label}${ext}`;
      };
      const buildPhotoFileName = (origName) => {
        const ext = getExt(origName);
        const pass = sanitize(form.passportNo || form.passport_no);
        const full = sanitize(form.fullName || form.full_name);
        const label = sanitize(subtype);
        return `${pass}_${full}_${label}${ext}`;
      };

      const meta = {
        type: kind,
        ...(kind === "document"
          ? { document_type: subtype }
          : { photo_type: subtype }),
        file_name:
          kind === "document"
            ? buildDocFileName(file?.name || f.file_name || f.name)
            : buildPhotoFileName(file?.name || f.file_name || f.name),
        file_path: f.file_path || f.temp_path,
        file_url: f.file_url,
        temp_path: f.temp_path || f.file_path,
        mime_type: f.mime_type,
        file_size: f.file_size,
      };
      setUploadedFiles((prev) => [...prev, meta]);
      const debugUrl = meta.file_url || meta.file_path || meta.temp_path;
      showSuccess("File uploaded");
    } catch (err) {
      showError(err.message || "Upload failed");
    }
  };

  // Define required fields by step (remarks excluded from required)
  const requiredFieldsByStep = useMemo(
    () => ({
      0: [
        "passportPlaceOfIssue",
        "passportExpiryDate",
        "fullName",
        "passportNo",
        "nationality",
        "dateOfBirth",
        "gender",
        "maritalStatus",
        "placeOfBirth",
        "heightCm",
        "weightKg",
        "religion",
        "email",
        "address",
        "contactNo",
        "vdrType",
        "medicalExpiryDate",
        "skills",
        "experience",
      ],
      1: [
        "nextOfKinName",
        "nextOfKinRelationship",
        "nextOfKinIdentificationNo",
        "nextOfKinContactNo",
        "nextOfKinAddress",
        "nextOfKinBankAccountNo",
        "nextOfKinBankName",
      ],
      // Steps 2 (Documents) and 3 (Photos) consist of uploads; leave as non-blocking for now
    }),
    []
  );

  // Generic validator for a set of fields
  const validateFields = (fields) => {
    const e = {};
    for (const k of fields) {
      if (!form[k] || String(form[k]).trim() === "")
        e[k] = "This field is required";
    }
    // Extra domain validations
    if ((form.passportNo || "").startsWith("DRAFT_")) {
      e.passportNo = "Replace draft passport number before submitting";
    }
    if (form.passportExpiryDate) {
      const expiry = new Date(form.passportExpiryDate);
      if (Number.isNaN(expiry.getTime())) {
        e.passportExpiryDate = "Enter a valid expiry date";
      } else {
        const sixMonthsFromNow = new Date();
        sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
        if (expiry < sixMonthsFromNow) {
          e.passportExpiryDate =
            "Passport expiry must be at least 6 months from today";
        }
      }
    }
    if (form.dateOfBirth) {
      const dob = new Date(form.dateOfBirth);
      if (!isNaN(dob.getTime())) {
        const now = new Date();
        let age = now.getFullYear() - dob.getFullYear();
        const m = now.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
        if (age < 18 || age > 45)
          e.dateOfBirth = "Age must be between 18 and 45 years";
      }
    }
    if (form.email && !emailRegex.test(String(form.email).trim())) {
      e.email = "Enter a valid email";
    }
    if (form.weightKg && Number(form.weightKg) > 100) {
      e.weightKg = "Weight cannot exceed 100 kg";
    }
    setErrors(e);
    return e;
  };

  // Validate current step fields and return pass/fail
  const validateCurrentStep = () => {
    const fields = requiredFieldsByStep[currentStep] || [];
    const e = validateFields(fields);
    // Only consider errors for the current step's fields when deciding navigation
    const stepHasErrors = fields.some((k) => !!e[k]);
    return !stepHasErrors;
  };

  const validateForSubmit = () => {
    // Validate all required fields across steps 0 and 1 before submit
    const allRequired = [
      ...(requiredFieldsByStep[0] || []),
      ...(requiredFieldsByStep[1] || []),
    ];
    const e = validateFields(allRequired);

    // Validate required documents (Highest Qualification optional)
    const requiredDocTypes = [
      "passport_copy",
      "next_of_kin_identification",
      "vaccine_certificate",
      "medical_certificate",
    ];
    const presentDocTypes = new Set([
      ...(form.documents || []).map((d) => d.document_type).filter(Boolean),
      ...uploadedFiles
        .filter((f) => f.type === "document")
        .map((f) => f.document_type)
        .filter(Boolean),
    ]);
    const missing = requiredDocTypes.filter((t) => !presentDocTypes.has(t));
    const labelForDocType = (type) => {
      const found = documentTypes.find((d) => d.value === type);
      return found?.label || (type || "").replace(/_/g, " ");
    };
    if (missing.length > 0) {
      const names = missing.map(labelForDocType);
      return `Missing required documents: ${names.join(", ")}`;
    }

    // Validate required photos - both passport and full_body are required
    const requiredPhotoTypes = ["passport", "full_body"];
    const presentPhotoTypes = new Set([
      ...(form.photos || []).map((p) => p.photo_type).filter(Boolean),
      ...uploadedFiles
        .filter((f) => f.type === "photo")
        .map((f) => f.photo_type)
        .filter(Boolean),
    ]);
    const missingPhotos = requiredPhotoTypes.filter(
      (t) => !presentPhotoTypes.has(t)
    );
    if (missingPhotos.length > 0) {
      const photoLabels = missingPhotos.map((t) =>
        t === "passport" ? "Passport Photo" : "Full Body Photo"
      );
      return `Missing required photos: ${photoLabels.join(", ")}`;
    }

    return Object.keys(e).length ? Object.values(e)[0] : "";
  };

  const persist = async (status) => {
    setSaving(true);
    try {
      if (status !== "draft") {
        const msg = validateForSubmit();
        if (msg) throw new Error(msg);
      }
      // Clean empty strings and coerce numeric fields to satisfy API schema
      const numericCoerce = (v) =>
        v === "" || v === null || v === undefined ? undefined : Number(v);
      // Debug: Log current form NOK fields before creating payload

      const rawPayload = {
        ...form,
        heightCm: numericCoerce(form.heightCm),
        weightKg: numericCoerce(form.weightKg),
        ...(status ? { status } : {}),
        uploadedFiles,
        appendUploads: uploadedFiles.length > 0,
      };

      // NOK fields that should be preserved even if empty
      const nokFields = [
        "nextOfKinName",
        "nextOfKinRelationship",
        "nextOfKinIdentificationNo",
        "nextOfKinAddress",
        "nextOfKinContactNo",
        "nextOfKinBankAccountNo",
        "nextOfKinBankName",
      ];

      // Filter out empty values but preserve NOK fields
      const payload = Object.fromEntries(
        Object.entries(rawPayload).filter(([key, v]) => {
          // Always include NOK fields, even if empty
          if (nokFields.includes(key)) return true;
          // For other fields, exclude empty strings, null, and undefined
          return v !== "" && v !== null && v !== undefined;
        })
      );

      // Debug: Log NOK fields in the final API payload

      // Debug: Check if NOK fields have actual values
      let res;
      if (isEditing) {
        res = await biodataAPI.updateBiodata(effectiveId, payload);
      } else {
        res = await biodataAPI.createBiodata(payload);
      }
      const ok = res && res.status !== "error" && res.success !== false;
      if (!ok) throw new Error(res.message || "Save failed");
      const savedBiodata = res.data || null;
      let updatedForm = form;
      if (savedBiodata) {
        updatedForm = mapBiodataToForm(savedBiodata);
        setForm(updatedForm);
        setUploadedFiles([]);
        setOriginalPassportNo(
          updatedForm.passportNo || updatedForm.passport_no || ""
        );
      }
      if (isEditing) {
        const refresh = await biodataAPI.getBiodataById(effectiveId);
        if (refresh.status === "success" && refresh.data) {
          const refreshed = mapBiodataToForm(refresh.data);
          setForm(refreshed);
          setOriginalPassportNo(
            refreshed.passportNo || refreshed.passport_no || ""
          );
        }
      }
      showSuccess(
        status === "draft"
          ? "Draft saved"
          : isAmend
          ? "Resubmitted for review"
          : isEditing
          ? "Updated"
          : "Submitted for review"
      );

      // If submitted for review (not draft), show additional warning after a short delay
      if (status && status !== "draft") {
        setTimeout(() => {
          showWarning(
            "Important: Once submitted, your details cannot be amended. Please ensure all information is correct."
          );
        }, 1500);
      }
      if (!isEditing && status === "draft" && savedBiodata?.id) {
        try {
          const newKey = `recruiter_biodata_step_edit_${savedBiodata.id}`;
          const existingStep = sessionStorage.getItem(storageStepKey);
          if (existingStep !== null) {
            sessionStorage.setItem(newKey, existingStep);
          }
        } catch (_) {}
        navigate(`/recruiter-biodata-edit/${savedBiodata.id}`, {
          replace: true,
        });
        return;
      }
      if (status && status !== "draft")
        navigate("/recruiter-biodata-management");
    } catch (e) {
      console.error("Persist draft error", e);
      showError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Temporarily disable autosave to improve typing UX and avoid noisy requests while we harden validation
  // const autosave = useMemo(() => {
  //   let t;
  //   return (payload) => {
  //     clearTimeout(t);
  //     t = setTimeout(() => {
  //       biodataAPI.updateBiodata(id, { ...payload, status: 'draft' }).catch(() => {});
  //     }, 12000);
  //   };
  // }, [id]);
  // useEffect(() => {
  //   if (!loading) autosave(form);
  // }, [form, loading, autosave]);

  // Validate passport on blur to avoid keystroke lag
  const handlePassportBlur = async () => {
    const p = (form.passportNo || "").trim();
    if (!p || p === originalPassportNo || p.startsWith("DRAFT_")) return;
    try {
      const res = await biodataAPI.checkPassportExists(p);
      if (res && (res.exists === true || res.exists === "true")) {
        setErrors((prev) => ({
          ...prev,
          passportNo: "Passport number already exists",
        }));
      } else {
        setErrors((prev) => ({ ...prev, passportNo: undefined }));
      }
    } catch (_) {}
  };

  if (loading) return null;

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar
        userRole="recruiter"
        userName="Recruiter"
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <RoleBasedSidebar
        userRole="recruiter"
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main
        className={`transition-all duration-300 pt-16 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-6 max-w-5xl mx-auto">
          <BreadcrumbNavigation userRole="recruiter" />
          <div className="mb-4 sticky top-16 z-20 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-secondary-100 py-3 px-1 rounded">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold">
                {isAmend
                  ? "Amend Biodata"
                  : isEditing
                  ? "Edit Biodata"
                  : "Create Biodata"}
              </h1>
              <div className="flex gap-2">
                <button
                  disabled={saving}
                  onClick={() => persist("draft")}
                  className="btn-secondary px-4 py-2 rounded flex items-center"
                >
                  {saving ? (
                    <Icon
                      name="Loader"
                      size={16}
                      className="animate-spin mr-2"
                    />
                  ) : (
                    <Icon name="Save" size={16} className="mr-2" />
                  )}{" "}
                  Save Draft
                </button>
                {isAmend && (
                  <button
                    disabled={saving}
                    onClick={() => persist("resubmitted")}
                    className="btn-primary px-4 py-2 rounded flex items-center"
                  >
                    {saving ? (
                      <Icon
                        name="Loader"
                        size={16}
                        className="animate-spin mr-2"
                      />
                    ) : (
                      <Icon name="Send" size={16} className="mr-2" />
                    )}{" "}
                    Resubmit for Review
                  </button>
                )}
                {!isAmend && !isEditing && (
                  <button
                    disabled={saving}
                    onClick={() => persist("pending")}
                    className="btn-primary px-4 py-2 rounded flex items-center"
                  >
                    {saving ? (
                      <Icon
                        name="Loader"
                        size={16}
                        className="animate-spin mr-2"
                      />
                    ) : (
                      <Icon name="Send" size={16} className="mr-2" />
                    )}{" "}
                    Submit for Review
                  </button>
                )}
              </div>
            </div>
          </div>

          <Stepper current={currentStep} steps={steps} />
          <div className="card p-6 mb-4 shadow-sm border border-gray-100">
            {currentStep === 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField label="Full Name" required error={errors.fullName}>
                  <TextInput
                    name="fullName"
                    value={form.fullName || ""}
                    onChange={onChange}
                  />
                </FormField>
                <FormField
                  label="Passport No"
                  required
                  error={errors.passportNo}
                >
                  <TextInput
                    name="passportNo"
                    value={form.passportNo || ""}
                    onChange={onChange}
                    onBlur={handlePassportBlur}
                  />
                </FormField>
                <FormField
                  label="Passport Place of Issue"
                  required
                  error={errors.passportPlaceOfIssue}
                >
                  <TextInput
                    name="passportPlaceOfIssue"
                    value={form.passportPlaceOfIssue || ""}
                    onChange={onChange}
                    placeholder="e.g., Malaysia, Singapore, Bangladesh"
                  />
                </FormField>
                <FormField
                  label="Passport Expiry Date"
                  required
                  error={errors.passportExpiryDate}
                >
                  <DateInput
                    name="passportExpiryDate"
                    value={form.passportExpiryDate || ""}
                    onChange={onChange}
                  />
                </FormField>
                <FormField
                  label="Nationality"
                  required
                  error={errors.nationality}
                >
                  <SelectInput
                    name="nationality"
                    value={form.nationality || ""}
                    onChange={onChange}
                  >
                    <option value="">Select nationality</option>
                    {nationalityOptions.map((n) => (
                      <option key={n.value} value={n.value}>
                        {n.label}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
                <FormField
                  label="Date of Birth"
                  required
                  error={errors.dateOfBirth}
                  hint={(() => {
                    const dob = form.dateOfBirth
                      ? new Date(form.dateOfBirth)
                      : null;
                    if (!dob || isNaN(dob.getTime())) return "";
                    const now = new Date();
                    let age = now.getFullYear() - dob.getFullYear();
                    const m = now.getMonth() - dob.getMonth();
                    if (m < 0 || (m === 0 && now.getDate() < dob.getDate()))
                      age--;
                    return age >= 0 ? `Age: ${age} years` : "";
                  })()}
                >
                  <DateInput
                    name="dateOfBirth"
                    value={form.dateOfBirth || ""}
                    onChange={onChange}
                  />
                </FormField>
                <FormField label="Gender" required error={errors.gender}>
                  <SelectInput
                    name="gender"
                    value={form.gender || "male"}
                    onChange={onChange}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </SelectInput>
                </FormField>
                <FormField
                  label="Marital Status"
                  required
                  error={errors.maritalStatus}
                >
                  <SelectInput
                    name="maritalStatus"
                    value={form.maritalStatus || "single"}
                    onChange={onChange}
                  >
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </SelectInput>
                </FormField>
                <FormField
                  label="Place of Birth"
                  required
                  error={errors.placeOfBirth}
                >
                  <TextInput
                    name="placeOfBirth"
                    value={form.placeOfBirth || ""}
                    onChange={onChange}
                  />
                </FormField>
                <FormField
                  label="Education Level"
                  error={errors.educationLevel}
                >
                  <SelectInput
                    name="educationLevel"
                    value={form.educationLevel || ""}
                    onChange={onChange}
                  >
                    <option value="">Select education level</option>
                    {educationOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
                <FormField label="Height (cm)" required error={errors.heightCm}>
                  <NumberWithUnit
                    name="heightCm"
                    value={form.heightCm || ""}
                    onChange={onChange}
                    unit="cm"
                  />
                </FormField>
                <FormField label="Weight (kg)" required error={errors.weightKg}>
                  <NumberWithUnit
                    name="weightKg"
                    value={form.weightKg || ""}
                    onChange={onChange}
                    unit="kg"
                  />
                </FormField>
                <FormField label="Religion" required error={errors.religion}>
                  <SelectInput
                    name="religion"
                    value={form.religion || ""}
                    onChange={onChange}
                  >
                    <option value="">Select religion</option>
                    {religionOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
                <FormField label="Email" required error={errors.email}>
                  <TextInput
                    name="email"
                    value={form.email || ""}
                    onChange={onChange}
                  />
                </FormField>
                <div className="md:col-span-2">
                  <FormField label="Address" required error={errors.address}>
                    <TextAreaInput
                      name="address"
                      value={form.address || ""}
                      onChange={onChange}
                      rows={3}
                    />
                  </FormField>
                </div>
                <FormField label="Contact No" required error={errors.contactNo}>
                  <TextInput
                    name="contactNo"
                    value={form.contactNo || ""}
                    onChange={onChange}
                  />
                </FormField>
                <FormField label="VDR Type" required error={errors.vdrType}>
                  <SelectInput
                    name="vdrType"
                    value={form.vdrType || "eQuotaSpecial"}
                    onChange={onChange}
                  >
                    <option value="eQuotaSpecial">eQuotaSpecial</option>
                  </SelectInput>
                </FormField>
                <FormField
                  label={
                    <span className="flex items-center justify-between">
                      <span>Medical Expiry Date</span>
                      <a
                        href="/assets/images/medical_expiry_guideline.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary text-xs hover:underline"
                      >
                        Preview guideline
                      </a>
                    </span>
                  }
                  required
                  error={errors.medicalExpiryDate}
                >
                  <DateInput
                    name="medicalExpiryDate"
                    value={form.medicalExpiryDate || ""}
                    onChange={onChange}
                  />
                </FormField>
                <div className="md:col-span-2">
                  <FormField label="Skills" required error={errors.skills}>
                    <TextInput
                      name="skills"
                      value={form.skills || ""}
                      onChange={onChange}
                    />
                  </FormField>
                </div>
                <div className="md:col-span-2">
                  <FormField
                    label="Experience"
                    required
                    error={errors.experience}
                  >
                    <TextAreaInput
                      name="experience"
                      value={form.experience || ""}
                      onChange={onChange}
                      rows={3}
                    />
                  </FormField>
                </div>
                <div className="md:col-span-2">
                  <FormField label="Remarks">
                    <TextAreaInput
                      name="remarks"
                      value={form.remarks || ""}
                      onChange={onChange}
                      rows={2}
                    />
                  </FormField>
                </div>
              </div>
            )}
            {currentStep === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField label="Name" required error={errors.nextOfKinName}>
                  <TextInput
                    name="nextOfKinName"
                    value={form.nextOfKinName || ""}
                    onChange={onChange}
                  />
                </FormField>
                <FormField
                  label="Relationship"
                  required
                  error={errors.nextOfKinRelationship}
                >
                  <SelectInput
                    name="nextOfKinRelationship"
                    value={form.nextOfKinRelationship || ""}
                    onChange={onChange}
                  >
                    <option value="">Select relationship</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Parent">Parent</option>
                    <option value="Child">Child</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Other Relative">Other Relative</option>
                    <option value="Friend">Friend</option>
                  </SelectInput>
                </FormField>
                <FormField
                  label="Identification No"
                  required
                  error={errors.nextOfKinIdentificationNo}
                >
                  <TextInput
                    name="nextOfKinIdentificationNo"
                    value={form.nextOfKinIdentificationNo || ""}
                    onChange={onChange}
                  />
                </FormField>
                <FormField
                  label="Contact No"
                  required
                  error={errors.nextOfKinContactNo}
                >
                  <TextInput
                    name="nextOfKinContactNo"
                    value={form.nextOfKinContactNo || ""}
                    onChange={onChange}
                  />
                </FormField>
                <div className="md:col-span-2">
                  <FormField
                    label="Address"
                    required
                    error={errors.nextOfKinAddress}
                  >
                    <TextAreaInput
                      name="nextOfKinAddress"
                      value={form.nextOfKinAddress || ""}
                      onChange={onChange}
                      rows={3}
                    />
                  </FormField>
                </div>
                <FormField
                  label="Bank Account No"
                  required
                  error={errors.nextOfKinBankAccountNo}
                >
                  <TextInput
                    name="nextOfKinBankAccountNo"
                    value={form.nextOfKinBankAccountNo || ""}
                    onChange={onChange}
                  />
                </FormField>
                <FormField
                  label="Bank Name"
                  required
                  error={errors.nextOfKinBankName}
                >
                  <TextInput
                    name="nextOfKinBankName"
                    value={form.nextOfKinBankName || ""}
                    onChange={onChange}
                  />
                </FormField>
              </div>
            )}
            {currentStep === 2 && (
              <div>
                <div className="bg-primary-50 p-4 rounded-lg border border-primary-200 mb-6">
                  <h3 className="text-lg font-semibold text-primary mb-4">
                    4. DOCUMENTS/PHOTOS UPLOAD
                  </h3>
                  <p className="text-sm text-text-secondary mb-4">
                    Please upload the required documents. Each document type has
                    its own upload area. Remove existing file to upload a new
                    one.
                  </p>

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-secondary-50">
                          <th className="border border-secondary-200 px-4 py-2 text-left text-sm font-medium text-text-primary">
                            Document Type
                          </th>
                          <th className="border border-secondary-200 px-4 py-2 text-left text-sm font-medium text-text-primary">
                            Upload
                          </th>
                          <th className="border border-secondary-200 px-4 py-2 text-left text-sm font-medium text-text-primary">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <DocumentUploadRow
                          label="FCL Passport or Identity Card"
                          documentType="passport_copy"
                          documents={form.documents}
                          uploadedFiles={uploadedFiles}
                          onUpload={handlePreUpload}
                        />
                        <DocumentUploadRow
                          label="Next of Kin Identity Card"
                          documentType="next_of_kin_identification"
                          documents={form.documents}
                          uploadedFiles={uploadedFiles}
                          onUpload={handlePreUpload}
                        />
                        <DocumentUploadRow
                          label="Skilled Certificate"
                          documentType="vaccine_certificate"
                          documents={form.documents}
                          uploadedFiles={uploadedFiles}
                          onUpload={handlePreUpload}
                        />
                        <DocumentUploadRow
                          label="Medical Certificate (FWCMS)"
                          documentType="medical_certificate"
                          documents={form.documents}
                          uploadedFiles={uploadedFiles}
                          onUpload={handlePreUpload}
                        />
                        <DocumentUploadRow
                          label="Highest Qualification"
                          documentType="highest_qualification"
                          documents={form.documents}
                          uploadedFiles={uploadedFiles}
                          onUpload={handlePreUpload}
                        />
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-semibold mb-2">
                    Existing Documents
                  </h4>
                  {(form.documents || []).length === 0 ? (
                    <div className="text-xs text-secondary-600">
                      No existing documents.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(form.documents || []).map((d) => (
                        <div
                          key={d.id}
                          className="border border-secondary-200 rounded-lg p-3 bg-white shadow-sm"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <FilePreview
                                file={d}
                                className="w-12 h-12"
                                showFileName={false}
                                onClick={() => handleFileClick(d)}
                              />
                              <div>
                                <div className="font-medium text-text-primary text-sm">
                                  {formatDocumentTypeLabel(d.document_type)}
                                </div>
                                <div className="text-xs text-text-secondary break-words max-w-[12rem]">
                                  {d.file_name}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleFileClick(d)}
                                className="text-primary text-xs hover:underline"
                              >
                                Preview
                              </button>
                              <button
                                type="button"
                                onClick={() => removeDocument(d.id)}
                                className="text-red-600 text-xs hover:underline"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-semibold mb-2">
                    Pending Uploads
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {uploadedFiles.filter((f) => f.type === "document")
                      .length === 0 && (
                      <span className="text-xs text-secondary-600">
                        No pending documents.
                      </span>
                    )}
                    {uploadedFiles
                      .filter((f) => f.type === "document")
                      .map((f, i) => (
                        <div
                          key={i}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-secondary-200 bg-secondary-50 text-xs"
                        >
                          <span className="font-medium">
                            {formatDocumentTypeLabel(f.document_type)}
                          </span>
                          <span className="text-secondary-700">
                            {f.file_name}
                          </span>
                          {f.file_url && (
                            <a
                              href={f.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              View
                            </a>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}
            {currentStep === 3 && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <FormField label="Passport Photo" required>
                    <input
                      className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-secondary-100 file:text-secondary-800 hover:file:bg-secondary-200"
                      type="file"
                      accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                      onChange={(e) =>
                        e.target.files?.[0] &&
                        handlePreUpload(e.target.files[0], "photo", "passport")
                      }
                    />
                  </FormField>
                  <FormField
                    label={
                      <span className="flex items-center justify-between">
                        <span>Full Body Photo</span>
                        <a
                          href="/assets/images/guideline_fullbody1.jpeg"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary text-xs hover:underline"
                        >
                          Preview guideline
                        </a>
                      </span>
                    }
                    required
                  >
                    <input
                      className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-secondary-100 file:text-secondary-800 hover:file:bg-secondary-200"
                      type="file"
                      accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                      onChange={(e) =>
                        e.target.files?.[0] &&
                        handlePreUpload(e.target.files[0], "photo", "full_body")
                      }
                    />
                  </FormField>
                </div>
                {(form.photos || []).length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-secondary-700 mb-2">
                      Existing Photos
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {(form.photos || []).map((p) => (
                        <div
                          key={p.id}
                          className="relative rounded border border-secondary-200 overflow-hidden bg-white"
                        >
                          <div className="p-2 text-xs font-medium text-secondary-700 capitalize">
                            {p.photo_type.replace("_", " ")}
                          </div>
                          <FilePreview
                            file={p}
                            className="w-full h-32 object-cover"
                            showFileName={false}
                            onClick={() => handleFileClick(p)}
                          />
                          <div className="absolute top-1 right-1 flex gap-1">
                            <button
                              type="button"
                              onClick={() => handleFileClick(p)}
                              className="bg-white bg-opacity-90 text-primary text-xs px-2 py-1 rounded hover:bg-opacity-100"
                            >
                              Preview
                            </button>
                            <button
                              type="button"
                              onClick={() => removePhoto(p.id)}
                              className="bg-white bg-opacity-90 text-red-600 text-xs px-2 py-1 rounded hover:bg-opacity-100"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {uploadedFiles
                    .filter((f) => f.type === "photo")
                    .map((f, i) => (
                      <div
                        key={i}
                        className="relative rounded border border-secondary-200 overflow-hidden bg-white"
                      >
                        <div className="p-2 text-xs font-medium text-secondary-700 capitalize">
                          {f.photo_type.replace("_", " ")}
                        </div>
                        <FilePreview
                          file={f}
                          className="w-full h-32 object-cover"
                          showFileName={false}
                          onClick={() => handleFileClick(f)}
                        />
                      </div>
                    ))}
                </div>
              </div>
            )}
            {currentStep === 4 && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Review</h3>
                <BiodataPreview form={form} uploadedFiles={uploadedFiles} />

                {/* Submit buttons in review step */}
                <div className="bg-white rounded-lg border border-secondary-200 p-6 mt-6">
                  <h4 className="text-md font-semibold text-text-primary mb-3">
                    Ready to Submit?
                  </h4>
                  <p className="text-sm text-text-secondary mb-4">
                    Please review all information carefully before submitting.
                    {isAmend
                      ? "Once submitted, the amended biodata will be sent to CLAB for review."
                      : "Once submitted, the biodata will be sent to CLAB for review."}
                  </p>
                  <div className="flex gap-3">
                    <button
                      disabled={saving}
                      onClick={() => persist("draft")}
                      className="btn-secondary px-4 py-2 rounded flex items-center"
                    >
                      {saving ? (
                        <Icon
                          name="Loader"
                          size={16}
                          className="animate-spin mr-2"
                        />
                      ) : (
                        <Icon name="Save" size={16} className="mr-2" />
                      )}
                      Save as Draft
                    </button>
                    {isAmend ? (
                      <button
                        disabled={saving}
                        onClick={() => persist("resubmitted")}
                        className="btn-primary px-4 py-2 rounded flex items-center"
                      >
                        {saving ? (
                          <Icon
                            name="Loader"
                            size={16}
                            className="animate-spin mr-2"
                          />
                        ) : (
                          <Icon name="Send" size={16} className="mr-2" />
                        )}
                        Resubmit for CLAB Review
                      </button>
                    ) : (
                      <button
                        disabled={saving}
                        onClick={() => persist("pending")}
                        className="btn-primary px-4 py-2 rounded flex items-center"
                      >
                        {saving ? (
                          <Icon
                            name="Loader"
                            size={16}
                            className="animate-spin mr-2"
                          />
                        ) : (
                          <Icon name="Send" size={16} className="mr-2" />
                        )}
                        Submit for CLAB Review
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <button
              disabled={currentStep === 0}
              onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
              className="btn-secondary px-4 py-2 rounded flex items-center"
            >
              <Icon name="ArrowLeft" size={16} className="mr-2" /> Previous
            </button>
            <button
              disabled={currentStep === steps.length - 1 || saving}
              onClick={async () => {
                const ok = validateCurrentStep();
                if (!ok) {
                  showError(
                    "Please fill all required fields before proceeding"
                  );
                  return;
                }
                // Auto-save to draft before moving to next step
                await persist("draft");
                setCurrentStep((s) => Math.min(steps.length - 1, s + 1));
              }}
              className="btn-secondary px-4 py-2 rounded flex items-center"
            >
              Next <Icon name="ArrowRight" size={16} className="ml-2" />
            </button>
          </div>
        </div>
      </main>
      {/* Global Notifier for success/error toasts */}
      <Notifier
        show={notifications.length > 0}
        type={notifications[notifications.length - 1]?.type || "info"}
        message={notifications[notifications.length - 1]?.message || ""}
        onClose={() =>
          removeNotification(notifications[notifications.length - 1]?.id)
        }
      />

      {/* File Preview Modal */}
      <FilePreviewModal
        file={selectedFile}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedFile(null);
        }}
      />
    </div>
  );
};

export default RecruiterBiodataEdit;
